/**
 * Validation and normalization for the `commands` catalog a robot-config payload MAY
 * advertise — the robot declaring which wire commands the UI should actually offer
 * (e.g. a quadruped's `posture.stand_up`/`posture.stand_down`/`cargo.*`, a rover's
 * plain `motion.stop` with no posture commands at all), instead of the UI hardcoding
 * one fixed control set for every robot. This module deliberately contains no UI
 * layout decisions — see robot-config.ts for how a resolved catalog reaches the UI,
 * and DriveControls.tsx/ConsolePage.tsx for how it's rendered.
 *
 * `commands` is an OPT-IN enhancement, not a required field: robot-config.ts only
 * calls this on payloads that actually include a `commands` array, and treats any
 * `{ ok: false }` result as "no catalog" rather than failing the whole robot-config —
 * a robot that hasn't been upgraded to advertise one yet keeps working exactly as
 * before, via the UI's own legacy fixed controls.
 */

import type { CommandMode, MotionProfile } from "../../types/commands";

export type { CommandMode, MotionProfile };

export interface RobotCommandEntry {
  command_id: string;
  type: string;
  mode: CommandMode;
  label?: string;
  params?: Record<string, unknown>;
}

export interface CommandCatalogSource {
  limits?: object;
  cargo_boxes?: Array<{ id: string }>;
  commands?: unknown;
}

export interface CommandCatalog {
  profile: MotionProfile;
  commands: RobotCommandEntry[];
  velocity: RobotCommandEntry | null;
  stop: RobotCommandEntry | null;
  postures: RobotCommandEntry[];
  cargo: RobotCommandEntry[];
}

export type CommandCatalogResult =
  { ok: true; catalog: CommandCatalog } | { ok: false; detail: string };

function isPositiveFinite(value: unknown): boolean {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

/**
 * A catalog only resolves to a profile when `limits` cleanly describes exactly one of
 * the two supported kinematics — a holonomic quadruped (max_vx/max_vy/max_vyaw, no
 * max_steer_angle) or an Ackermann rover (max_vx/max_steer_angle, no max_vy/max_vyaw).
 * Anything else (neither shape, both, or a mixed set) is ambiguous and is rejected.
 */
function resolveProfile(source: CommandCatalogSource): MotionProfile | null {
  const limits = (source.limits ?? {}) as Record<string, unknown>;
  const hasDog = ["max_vx", "max_vy", "max_vyaw"].every((key) => isPositiveFinite(limits[key]));
  const hasRover = ["max_vx", "max_steer_angle"].every((key) => isPositiveFinite(limits[key]));
  if (hasDog === hasRover) return null;
  if (hasDog && "max_steer_angle" in limits) return null;
  if (hasRover && ("max_vy" in limits || "max_vyaw" in limits)) return null;
  return hasDog ? "holonomic" : "ackermann";
}

function expectedMode(type: string): CommandMode | null {
  if (type === "motion.velocity") return "stream";
  if (
    type === "motion.stop" ||
    type === "cargo.open" ||
    type === "cargo.close" ||
    type.startsWith("posture.")
  ) {
    return "edge";
  }
  return null;
}

export function resolveCommandCatalog(source: CommandCatalogSource): CommandCatalogResult {
  const profile = resolveProfile(source);
  if (!profile) {
    return { ok: false, detail: "limits do not describe exactly one supported motion profile" };
  }
  if (!Array.isArray(source.commands)) {
    return { ok: false, detail: "commands must be an array" };
  }

  const boxIds = new Set((source.cargo_boxes ?? []).map((box) => box.id));
  const ids = new Set<string>();
  const commands: RobotCommandEntry[] = [];

  for (const [index, raw] of source.commands.entries()) {
    if (!raw || typeof raw !== "object") {
      return { ok: false, detail: `commands[${index}] must be an object` };
    }
    const rec = raw as Record<string, unknown>;
    const commandId = typeof rec.command_id === "string" ? rec.command_id.trim() : "";
    const type = typeof rec.type === "string" ? rec.type.trim() : "";
    const mode = rec.mode === "stream" || rec.mode === "edge" ? rec.mode : null;
    const requiredMode = expectedMode(type);

    if (!commandId || !type || !mode) {
      return { ok: false, detail: `commands[${index}] has invalid required fields` };
    }
    if (ids.has(commandId)) {
      return { ok: false, detail: `duplicate command_id: ${commandId}` };
    }
    if (!requiredMode || mode !== requiredMode) {
      return { ok: false, detail: `unsupported type/mode: ${type}/${mode}` };
    }
    if (mode === "edge" && (typeof rec.label !== "string" || !rec.label.trim())) {
      return { ok: false, detail: `label is required for edge command: ${commandId}` };
    }

    const params =
      rec.params && typeof rec.params === "object"
        ? { ...(rec.params as Record<string, unknown>) }
        : undefined;
    if (type === "cargo.open" || type === "cargo.close") {
      const boxId = typeof params?.box_id === "string" ? params.box_id.trim() : "";
      if (!boxId || !boxIds.has(boxId)) {
        return { ok: false, detail: `invalid cargo box_id for command: ${commandId}` };
      }
      if (params) params.box_id = boxId;
    }

    ids.add(commandId);
    commands.push({
      command_id: commandId,
      type,
      mode,
      ...(typeof rec.label === "string" && rec.label.trim() ? { label: rec.label.trim() } : {}),
      ...(params ? { params } : {}),
    });
  }

  const velocityCommands = commands.filter((command) => command.type === "motion.velocity");
  const stopCommands = commands.filter((command) => command.type === "motion.stop");
  if (velocityCommands.length > 1) {
    return { ok: false, detail: "motion.velocity must not be duplicated" };
  }
  if (stopCommands.length > 1) {
    return { ok: false, detail: "motion.stop must not be duplicated" };
  }

  return {
    ok: true,
    catalog: {
      profile,
      commands,
      velocity: velocityCommands[0] ?? null,
      stop: stopCommands[0] ?? null,
      postures: commands.filter((command) => command.type.startsWith("posture.")),
      cargo: commands.filter(
        (command) => command.type === "cargo.open" || command.type === "cargo.close"
      ),
    },
  };
}
